/**
 * 媒体大图预览 Lightbox（图片缩放/旋转 + 视频播放 + 上下张切换 + 键盘 Esc·←·→）
 *
 * 由 records/MediaResources 与 records/FixModal 共用：
 *  - MediaResources：跨轮次扁平列表预览
 *  - FixModal：现有媒体缩略图点击预览
 */
import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  CloseOutlined,
  ExportOutlined,
  LeftOutlined,
  RedoOutlined,
  ReloadOutlined,
  RightOutlined,
  UndoOutlined,
  ZoomInOutlined,
  ZoomOutOutlined
} from '@ant-design/icons'

export interface MediaLightboxItem {
  photo_url: string
  photo_file?: string
  photo_seq: number | string
  photo_time?: string
  media_type?: 0 | 1
  /** 来源轮次描述（如"第一次提交"），可选 */
  roundLabel?: string
}

export function detectIsVideo(item: MediaLightboxItem): boolean {
  if (item.media_type === 1) return true
  const url = (item.photo_url || item.photo_file || '').toLowerCase()
  return /\.(mp4|mov|webm|m3u8|avi|mkv|m4v|3gp)(\?|$|#)/.test(url)
}

interface MediaLightboxProps {
  list: MediaLightboxItem[]
  index: number
  onClose: () => void
  onPrev: () => void
  onNext: () => void
}

const ToolButton: React.FC<{
  onClick: () => void
  children: React.ReactNode
  title?: string
  disabled?: boolean
}> = ({ onClick, children, title, disabled }) => (
  <span
    title={title}
    onClick={(e) => {
      e.stopPropagation()
      if (!disabled) onClick()
    }}
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 36,
      height: 36,
      borderRadius: 999,
      background: 'rgba(255,255,255,0.08)',
      color: disabled ? 'rgba(255,255,255,0.3)' : '#fff',
      cursor: disabled ? 'not-allowed' : 'pointer',
      fontSize: 16,
      transition: 'background .15s'
    }}
    onMouseEnter={(e) => {
      if (!disabled)
        (e.currentTarget as HTMLSpanElement).style.background =
          'rgba(255,255,255,0.18)'
    }}
    onMouseLeave={(e) => {
      ;(e.currentTarget as HTMLSpanElement).style.background =
        'rgba(255,255,255,0.08)'
    }}
  >
    {children}
  </span>
)

const MediaLightbox: React.FC<MediaLightboxProps> = ({
  list,
  index,
  onClose,
  onPrev,
  onNext
}) => {
  const item = list[index]
  const [scale, setScale] = useState(1)
  const [rotate, setRotate] = useState(0)
  const [translate, setTranslate] = useState({ x: 0, y: 0 })
  const [dragging, setDragging] = useState(false)
  const dragStartRef = useRef<{
    x: number
    y: number
    tx: number
    ty: number
  } | null>(null)

  useEffect(() => {
    setScale(1)
    setRotate(0)
    setTranslate({ x: 0, y: 0 })
  }, [index])

  // scale 回到 1 时复位平移
  useEffect(() => {
    if (scale <= 1) setTranslate({ x: 0, y: 0 })
  }, [scale])

  // 拖拽：scale > 1 时，按住图片可拖动
  const handleImageMouseDown = useCallback(
    (e: React.MouseEvent<HTMLImageElement>) => {
      if (scale <= 1) return
      e.preventDefault()
      e.stopPropagation()
      dragStartRef.current = {
        x: e.clientX,
        y: e.clientY,
        tx: translate.x,
        ty: translate.y
      }
      setDragging(true)
    },
    [scale, translate.x, translate.y]
  )

  useEffect(() => {
    if (!dragging) return
    const onMove = (ev: MouseEvent) => {
      const start = dragStartRef.current
      if (!start) return
      setTranslate({
        x: start.tx + (ev.clientX - start.x),
        y: start.ty + (ev.clientY - start.y)
      })
    }
    const onUp = () => {
      dragStartRef.current = null
      setDragging(false)
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
    return () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
    }
  }, [dragging])

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
      else if (e.key === 'ArrowLeft') onPrev()
      else if (e.key === 'ArrowRight') onNext()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onClose, onPrev, onNext])

  useEffect(() => {
    const orig = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = orig
    }
  }, [])

  if (!item) return null
  const isVideo = detectIsVideo(item)
  const hasPrev = index > 0
  const hasNext = index < list.length - 1

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1100,
        background: 'rgba(0,0,0,0.96)',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {/* 顶部条 */}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          flexShrink: 0,
          padding: '14px 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          color: '#fff',
          position: 'relative',
          zIndex: 2,
          background: 'rgba(0,0,0,0.96)'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 14, fontFamily: 'Inter, system-ui' }}>
            {index + 1} / {list.length}
          </span>
          {item.roundLabel && (
            <>
              <span
                style={{
                  width: 1,
                  height: 12,
                  background: 'rgba(255,255,255,.3)'
                }}
              />
              <span style={{ fontSize: 13, color: 'rgba(255,255,255,.85)' }}>
                {item.roundLabel}
              </span>
            </>
          )}
          <span
            style={{ width: 1, height: 12, background: 'rgba(255,255,255,.3)' }}
          />
          <span
            style={{
              fontSize: 12,
              color: 'rgba(255,255,255,.6)',
              fontFamily: 'Inter, system-ui'
            }}
          >
            #{item.photo_seq}
          </span>
          {item.photo_time && (
            <>
              <span
                style={{
                  width: 1,
                  height: 12,
                  background: 'rgba(255,255,255,.3)'
                }}
              />
              <span style={{ fontSize: 12, color: 'rgba(255,255,255,.6)' }}>
                {item.photo_time}
              </span>
            </>
          )}
        </div>
        <ToolButton onClick={onClose} title="关闭 (Esc)">
          <CloseOutlined />
        </ToolButton>
      </div>

      {/* 主体（overflow:hidden 防止放大后的图片溢出遮挡上下工具栏） */}
      <div
        onClick={onClose}
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          minHeight: 0,
          padding: '0 64px',
          overflow: 'hidden',
          zIndex: 1
        }}
      >
        {hasPrev && (
          <span
            onClick={(e) => {
              e.stopPropagation()
              onPrev()
            }}
            style={{
              position: 'absolute',
              left: 16,
              top: '50%',
              transform: 'translateY(-50%)',
              width: 44,
              height: 44,
              borderRadius: 999,
              background: 'rgba(255,255,255,0.12)',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              fontSize: 20
            }}
          >
            <LeftOutlined />
          </span>
        )}

        <div
          onClick={(e) => e.stopPropagation()}
          style={{ maxWidth: '100%', maxHeight: '100%' }}
        >
          {isVideo ? (
            <video
              src={item.photo_url}
              controls
              autoPlay
              style={{
                maxWidth: '100%',
                maxHeight: 'calc(100vh - 160px)',
                background: '#000',
                borderRadius: 4
              }}
            />
          ) : (
            <img
              src={item.photo_url}
              alt=""
              onMouseDown={handleImageMouseDown}
              style={{
                maxWidth: '100%',
                maxHeight: 'calc(100vh - 160px)',
                transform: `translate(${translate.x}px, ${translate.y}px) scale(${scale}) rotate(${rotate}deg)`,
                transition: dragging ? 'none' : 'transform .2s ease',
                userSelect: 'none',
                cursor: scale > 1 ? (dragging ? 'grabbing' : 'grab') : 'default'
              }}
              draggable={false}
            />
          )}
        </div>

        {hasNext && (
          <span
            onClick={(e) => {
              e.stopPropagation()
              onNext()
            }}
            style={{
              position: 'absolute',
              right: 16,
              top: '50%',
              transform: 'translateY(-50%)',
              width: 44,
              height: 44,
              borderRadius: 999,
              background: 'rgba(255,255,255,0.12)',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              fontSize: 20
            }}
          >
            <RightOutlined />
          </span>
        )}
      </div>

      {/* 底部工具栏（仅图片显示） */}
      {!isVideo && (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            flexShrink: 0,
            padding: '14px 24px',
            display: 'flex',
            justifyContent: 'center',
            gap: 12,
            position: 'relative',
            zIndex: 2,
            background: 'rgba(0,0,0,0.96)'
          }}
        >
          <ToolButton
            onClick={() => setScale((s) => Math.max(0.2, s - 0.2))}
            title="缩小"
            disabled={scale <= 0.2}
          >
            <ZoomOutOutlined />
          </ToolButton>
          <ToolButton
            onClick={() => setScale((s) => Math.min(5, s + 0.2))}
            title="放大"
            disabled={scale >= 5}
          >
            <ZoomInOutlined />
          </ToolButton>
          <ToolButton
            onClick={() => {
              setScale(1)
              setRotate(0)
              setTranslate({ x: 0, y: 0 })
            }}
            title="复位"
          >
            <ReloadOutlined />
          </ToolButton>
          <ToolButton onClick={() => setRotate((r) => r - 90)} title="左旋转">
            <UndoOutlined />
          </ToolButton>
          <ToolButton onClick={() => setRotate((r) => r + 90)} title="右旋转">
            <RedoOutlined />
          </ToolButton>
          <a
            href={item.photo_url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            title="在新窗口打开"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 36,
              height: 36,
              borderRadius: 999,
              background: 'rgba(255,255,255,0.08)',
              color: '#fff',
              fontSize: 16
            }}
          >
            <ExportOutlined />
          </a>
        </div>
      )}
    </div>
  )
}

export default MediaLightbox
